'use client';

import * as React from 'react';

import { cn } from './utils';

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  wrapperRef?: React.RefObject<HTMLDivElement | null>;
  stickyFirstColumn?: boolean;
  hideFirstColumnShadow?: boolean;
  /**
   * Number of columns to stick from the right.
   * Adds: sticky positioning, z-index layering, background colors, and shadow on the boundary.
   * Consumers must still set `right-*` offsets and `w-*` widths on individual cells.
   * Background colors default to layer-02 (header) and layer-01 (body).
   * Override via CSS custom properties: `--table-sticky-header-bg`, `--table-sticky-cell-bg`.
   */
  stickyLastColumns?: number;
  hideLastColumnShadow?: boolean;
  hideHeaderBorder?: boolean;
  withScrollbar?: boolean;
  onTableWrapperRef?: (ref: HTMLDivElement | null) => void;
};

function buildStickyLastColStyles(scopeId: string, count: number, hideShadow?: boolean): string {
  const scope = `[data-table-scope="${scopeId}"]`;
  const n = count;

  return [
    // Sticky positioning for last N columns
    `${scope} th:nth-last-child(-n+${n}){position:sticky;z-index:20;background-color:var(--table-sticky-header-bg)}`,
    `${scope} td:nth-last-child(-n+${n}){position:sticky;z-index:10;background-color:var(--table-sticky-cell-bg)}`,
    // Shadow on the boundary column (outermost sticky)
    !hideShadow &&
      `${scope} th:nth-last-child(${n})::before,${scope} td:nth-last-child(${n})::before{content:'';display:block;position:absolute;left:0.5rem;top:0;bottom:0;width:0.5rem;transform:translateX(-100%);box-shadow:-4px 0 6px 0 rgba(11,10,8,0.5)}`,
  ]
    .filter(Boolean)
    .join('');
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      wrapperClassName,
      wrapperStyle,
      wrapperRef,
      stickyFirstColumn,
      hideFirstColumnShadow,
      stickyLastColumns,
      hideLastColumnShadow,
      hideHeaderBorder,
      withScrollbar = true,
      onTableWrapperRef,
      ...props
    },
    ref,
  ) => {
    const internalRef = React.useRef<HTMLDivElement>(null);
    const scopeId = React.useId().replace(/:/g, '');

    // Combine refs and call onTableWrapperRef callback
    const setWrapperRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        // Update internal ref
        (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;

        // Update external wrapperRef if provided
        if (wrapperRef) {
          (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }

        // Call callback if provided
        onTableWrapperRef?.(node);
      },
      [wrapperRef, onTableWrapperRef],
    );

    // Sticky first column classes with proper z-index layering
    const stickyFirstColClasses = stickyFirstColumn
      ? cn(
          // Header first cell: relative for pseudo-element, z-20 to layer above sticky header (z-10)
          '[&_th:first-child]:relative [&_th:first-child]:sticky [&_th:first-child]:left-0 [&_th:first-child]:z-20 [&_th:first-child]:bg-[var(--table-sticky-header-bg)]',
          // Body first cells: relative for pseudo-element, z-10
          '[&_td:first-child]:relative [&_td:first-child]:sticky [&_td:first-child]:left-0 [&_td:first-child]:z-10 [&_td:first-child]:bg-[var(--table-sticky-cell-bg)]',
          // Shadow pseudo-elements (only when not hidden)
          !hideFirstColumnShadow && [
            // Header shadow pseudo-element with required content and block display
            '[&_th:first-child]:after:content-[""] [&_th:first-child]:after:block',
            '[&_th:first-child]:after:absolute [&_th:first-child]:after:right-0 [&_th:first-child]:after:inset-y-0',
            '[&_th:first-child]:after:w-2 [&_th:first-child]:after:shadow-[4px_0_6px_0_rgba(11,10,8,0.5)]',
            // Body shadow pseudo-element with required content and block display
            '[&_td:first-child]:after:content-[""] [&_td:first-child]:after:block',
            '[&_td:first-child]:after:absolute [&_td:first-child]:after:right-0 [&_td:first-child]:after:inset-y-0',
            '[&_td:first-child]:after:w-2 [&_td:first-child]:after:shadow-[4px_0_6px_0_rgba(11,10,8,0.5)]',
          ],
        )
      : '';

    // Header border toggle
    const headerBorderClasses = hideHeaderBorder ? '[&_thead_tr]:border-b-0' : '';

    // Scrollbar styling (enabled by default)
    const scrollbarClasses = withScrollbar ? 'scrollbar-thin scrollbar-track-layer-1 scrollbar-thumb-layer-3' : '';

    // CSS custom properties for sticky column backgrounds
    const needsStickyVars = !!stickyFirstColumn || (stickyLastColumns && stickyLastColumns > 0);
    const hasStickyLastCols = stickyLastColumns && stickyLastColumns > 0;

    return (
      <div
        ref={setWrapperRef}
        className={cn('overflow-x-auto max-h-[712px] overflow-y-auto', scrollbarClasses, wrapperClassName)}
        style={
          {
            ...(needsStickyVars && {
              '--table-sticky-header-bg': 'var(--layer-02)',
              '--table-sticky-cell-bg': 'var(--layer-01)',
            }),
            ...wrapperStyle,
          } as React.CSSProperties
        }
      >
        {hasStickyLastCols && (
          <style
            dangerouslySetInnerHTML={{
              __html: buildStickyLastColStyles(scopeId, stickyLastColumns, hideLastColumnShadow),
            }}
          />
        )}
        <table
          ref={ref}
          data-table-scope={hasStickyLastCols ? scopeId : undefined}
          className={cn('w-full min-w-max caption-bottom', stickyFirstColClasses, headerBorderClasses, className)}
          {...props}
        />
      </div>
    );
  },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('sticky top-0 z-10 bg-layer-2', className)} {...props} />,
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />,
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn('border-t border-border bg-layer-1 font-medium', className)} {...props} />
  ),
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn('border-b border-border transition-colors hover:bg-layer-2/30 data-[state=selected]:bg-layer-2', className)}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn('px-4 py-2 text-left text-sm font-medium text-text-primary align-middle [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn('px-4 py-2 align-middle text-text-secondary [&:has([role=checkbox])]:pr-0', className)} {...props} />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => <caption ref={ref} className={cn('mt-4 text-sm text-text-secondary', className)} {...props} />,
);
TableCaption.displayName = 'TableCaption';

export { Table, TableBody, TableCaption, TableCell, TableFooter, TableHead, TableHeader, TableRow };
